// src/components/PostsView.tsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    CardMedia,
    Avatar,
    Chip,
    Stack,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SchoolIcon from '@mui/icons-material/School';
import PublicIcon from '@mui/icons-material/Public';
import ChurchIcon from '@mui/icons-material/Church';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { getPostsByDate, deletePost, togglePostFeatured, moderatePost } from '../services/liturgicalService';
import { LiturgicalPost } from '../types/liturgical';

interface PostsViewProps {
    day: number;
    month: number;
    isAdmin?: boolean;
    onRefresh?: () => void;
}

const POST_TYPE_CONFIG = {
    reflection: { label: 'Réflexion', icon: <LightbulbOutlinedIcon />, color: '#2196F3' },
    prayer: { label: 'Prière', icon: <AutoAwesomeIcon />, color: '#9C27B0' },
    testimony: { label: 'Témoignage', icon: <FavoriteIcon />, color: '#E91E63' },
    teaching: { label: 'Enseignement', icon: <SchoolIcon />, color: '#FF9800' },
};

const VISIBILITY_CONFIG = {
    public: { label: 'Public', icon: <PublicIcon />, color: 'success' },
    church: { label: 'Église', icon: <ChurchIcon />, color: 'info' },
    private: { label: 'Privé', icon: <LockIcon />, color: 'default' },
};

const PostsView: React.FC<PostsViewProps> = ({ day, month, isAdmin = true, onRefresh }) => {
    const { t } = useTranslation();
    const [posts, setPosts] = useState<LiturgicalPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [postToDelete, setPostToDelete] = useState<LiturgicalPost | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedPostForMenu, setSelectedPostForMenu] = useState<LiturgicalPost | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const postsRes = await getPostsByDate(month, day);
            setPosts(postsRes);
        } catch (err: any) {
            console.error('Failed to fetch liturgical posts:', err);
            if (err.response?.status !== 404) {
                setError('Failed to fetch liturgical posts. See console for details.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [day, month]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, post: LiturgicalPost) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedPostForMenu(post);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedPostForMenu(null);
    };

    const handleToggleFeatured = async (post: LiturgicalPost) => {
        setActionLoading(true);
        handleMenuClose();
        try {
            await togglePostFeatured(post.id);
            fetchData();
            onRefresh?.();
        } catch (err: any) {
            console.error('Failed to toggle post featured:', err);
            setError(t('admin.posts.errorFeaturing'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleModerate = async (post: LiturgicalPost, status: 'approved' | 'rejected') => {
        setActionLoading(true);
        handleMenuClose();
        try {
            await moderatePost(post.id, { moderation_status: status });
            fetchData();
            onRefresh?.();
        } catch (err: any) {
            console.error('Failed to moderate post:', err);
            setError(t('admin.posts.errorModerating'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteClick = (post: LiturgicalPost) => {
        handleMenuClose();
        setPostToDelete(post);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!postToDelete) return;
        setActionLoading(true);
        try {
            await deletePost(postToDelete.id);
            setDeleteDialogOpen(false);
            setPostToDelete(null);
            fetchData();
            onRefresh?.();
        } catch (err: any) {
            console.error('Failed to delete post:', err);
            setError(t('admin.posts.errorDeleting'));
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom>{t('admin.posts.title')} {day}/{month}</Typography>
            {posts.length > 0 ? (
                posts.map(post => {
                    const postTypeConfig = POST_TYPE_CONFIG[post.post_type as keyof typeof POST_TYPE_CONFIG];
                    const visibilityConfig = VISIBILITY_CONFIG[post.visibility as keyof typeof VISIBILITY_CONFIG];

                    return (
                        <Card key={post.id} sx={{ mb: 3 }}>
                            {/* Header avec auteur */}
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ mr: 1.5, bgcolor: postTypeConfig?.color }}>
                                            {post.user?.name?.charAt(0) || 'U'}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {post.user?.name || 'Utilisateur inconnu'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(post.created_at).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {postTypeConfig && (
                                            <Chip
                                                icon={postTypeConfig.icon}
                                                label={postTypeConfig.label}
                                                size="small"
                                                sx={{
                                                    bgcolor: postTypeConfig.color,
                                                    color: 'white'
                                                }}
                                            />
                                        )}
                                        {visibilityConfig && (
                                            <Chip
                                                icon={visibilityConfig.icon}
                                                label={visibilityConfig.label}
                                                size="small"
                                                color={visibilityConfig.color as any}
                                                variant="outlined"
                                            />
                                        )}
                                        {/* Admin Actions Menu */}
                                        {isAdmin && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuOpen(e, post)}
                                                disabled={actionLoading}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Stack>

                                {/* Titre si présent */}
                                {post.title && (
                                    <Typography variant="h6" gutterBottom>
                                        {post.title}
                                    </Typography>
                                )}

                                {/* Contenu */}
                                <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                                    {post.content}
                                </Typography>

                                {/* Média */}
                                {post.media && post.media.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        {post.media.map((media, index) => (
                                            <Box key={media.id} sx={{ mb: 1 }}>
                                                {media.file_type === 'image' && (
                                                    <CardMedia
                                                        component="img"
                                                        image={media.file_url}
                                                        alt={media.alt_text || post.title || 'Image'}
                                                        sx={{ borderRadius: 1, maxHeight: 400, objectFit: 'cover' }}
                                                    />
                                                )}
                                                {media.file_type === 'video' && (
                                                    <video
                                                        controls
                                                        style={{ width: '100%', maxHeight: '400px', borderRadius: '4px' }}
                                                    >
                                                        <source src={media.file_url} type="video/mp4" />
                                                        Votre navigateur ne supporte pas la lecture vidéo.
                                                    </video>
                                                )}
                                                {media.caption && (
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                        {media.caption}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {/* Footer avec stats */}
                                <Stack direction="row" spacing={3} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {post.reactions_count} Réactions
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {post.comments_count} Commentaires
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })
            ) : (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('admin.posts.noPosts')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('admin.posts.noPosts')} {day}/{month}
                    </Typography>
                </Paper>
            )}

            {/* Admin Actions Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => selectedPostForMenu && handleToggleFeatured(selectedPostForMenu)}>
                    <ListItemIcon>
                        <StarIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('admin.posts.feature')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => selectedPostForMenu && handleModerate(selectedPostForMenu, 'approved')}>
                    <ListItemIcon>
                        <CheckCircleIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText>{t('admin.posts.approve')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => selectedPostForMenu && handleModerate(selectedPostForMenu, 'rejected')}>
                    <ListItemIcon>
                        <CancelIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>{t('admin.posts.reject')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => selectedPostForMenu && handleDeleteClick(selectedPostForMenu)}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>{t('common.delete')}</ListItemText>
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !actionLoading && setDeleteDialogOpen(false)}
            >
                <DialogTitle>{t('admin.posts.deleteConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('admin.posts.deleteConfirmMessage')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={actionLoading}
                    >
                        {actionLoading ? t('common.loading') : t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PostsView;
